import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';

/** Case-insensitive "name contains the search", true for an empty search */
export const matchesSearch = (name: string | null | undefined, search: string): boolean =>
  !search.trim() || (name ?? '').toLowerCase().includes(search.trim().toLowerCase());

type Props = {
  title: React.ReactNode;
  // Right of the search box, e.g. the section's add button
  actions?: React.ReactNode;
  searchPlaceholder?: string;
  defaultOpen?: boolean;
  // Receives the search, to show only the matching entries
  children: (search: string) => React.ReactNode;
};

/**
 * A User Attributes block that opens / closes with its arrow and filters its entries with its search box.
 * A closed section is hidden, not unmounted: what was typed in it (and the badges' unsaved changes) is kept.
 */
export default function UserAttributeSection({
  title,
  actions,
  searchPlaceholder = 'Search by name',
  defaultOpen = true,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [search, setSearch] = useState('');

  return (
    <Form.Group className="uv-section">
      <div className="d-flex justify-content-between align-items-center uv-items-header">
        <div className="uv-items-header-side">
          <Button
            variant="link"
            className="uv-section-toggle"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? 'Close section' : 'Open section'}
          >
            <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
          </Button>
        </div>
        <Form.Label className="uv-items-title uv-section-title" onClick={() => setOpen(!open)}>
          {title}
        </Form.Label>
        <div className="uv-items-header-side d-flex justify-content-end align-items-center uv-section-tools">
          {open && (
            <Form.Control
              size="sm"
              type="search"
              className="uv-section-search"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          )}
          {actions}
        </div>
      </div>
      <div hidden={!open}>{children(search)}</div>
    </Form.Group>
  );
}
